/**
 * Tenant Configuration
 *
 * Baseline tenant configs. Site types that ship a bundle under `customize/`
 * supply their own config there and are merged in below, so this file never
 * names a customized tenant.
 *
 * Frontend counterpart to the backend's config/tenants.php.
 */
import { getTenantCustomizations } from '@customize/registry';

export interface TenantTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor?: string;
  textColor?: string;
  logo?: string;
  favicon?: string;
}

export interface TenantFeatures {
  [key: string]: boolean;
}

export interface TenantSettings {
  dateFormat: string;
  currency: string;
  language: string;
  [key: string]: unknown;
}

export interface TenantConfig {
  name: string;
  code: string;
  description: string;
  enabled: boolean;
  theme: TenantTheme;
  features: TenantFeatures;
  settings: TenantSettings;
}

export const DEFAULT_TENANT = 'default';

const baseTenantConfigs: Record<string, TenantConfig> = {
  default: {
    name: 'Default',
    code: 'default',
    description: 'Default tenant configuration',
    enabled: true,
    theme: {
      primaryColor: '#3b82f6',
      secondaryColor: '#10b981',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
    },
    features: {
      patients: true,
      consents: true,
      rezepts: true,
      visits: true,
    },
    settings: {
      dateFormat: 'YYYY/MM/DD',
      currency: '¥',
      language: 'ja',
    },
  },

  medix: {
    name: 'Medix',
    code: 'medix',
    description: 'Medix customized site',
    enabled: true,
    theme: {
      primaryColor: '#7c3aed',
      secondaryColor: '#a78bfa',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      logo: '/assets/tenants/medix/logo.png',
    },
    features: {
      patients: true,
      consents: true,
      rezepts: true,
      visits: true,
    },
    settings: {
      dateFormat: 'YYYY/MM/DD',
      currency: '¥',
      language: 'ja',
    },
  },

};

/**
 * Baseline configs plus every config contributed by `customize/`.
 *
 * Resolved lazily and memoised: `customize/` modules import back into `src/`,
 * and doing the merge at module-evaluation time would read the registry while
 * that import cycle is still in flight.
 */
let mergedConfigs: Record<string, TenantConfig> | null = null;

export const getTenantConfigs = (): Record<string, TenantConfig> => {
  if (!mergedConfigs) {
    mergedConfigs = getTenantCustomizations().reduce(
      (acc, tenant) => (tenant.config ? { ...acc, [tenant.code]: tenant.config } : acc),
      baseTenantConfigs
    );
  }
  return mergedConfigs;
};

/**
 * Get tenant configuration by code
 */
export const getTenantConfig = (tenantCode: string): TenantConfig => {
  const configs = getTenantConfigs();
  return configs[tenantCode] || configs[DEFAULT_TENANT];
};

/**
 * Check if tenant exists and is enabled
 */
export const isTenantEnabled = (tenantCode: string): boolean => {
  return getTenantConfigs()[tenantCode]?.enabled ?? false;
};

/**
 * Get all active tenants
 */
export const getActiveTenants = (): TenantConfig[] => {
  return Object.values(getTenantConfigs()).filter(config => config.enabled);
};
